import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useCharacterStore } from '@/engine/state';

/**
 * Capa visual a nivel raíz que reacciona a cambios del personaje:
 *  - Flash rojo + shake al recibir daño (delta negativo de vida).
 *  - Viñeta roja persistente cuando la vida está baja (<40% del máximo),
 *    intensificándose hacia 0.
 *  - Banner ceremonial al subir de nivel.
 *
 * Se monta sobre todo lo demás pero en z-index menor que los Game Over
 * y cosmic, así que éstos la tapan cuando corresponde.
 */
export function ScreenEffects() {
  const vida = useCharacterStore((s) => s.personaje.stats.vida);
  const vidaMax = useCharacterStore((s) => s.personaje.stats.vidaMax);
  const nivel = useCharacterStore((s) => s.personaje.nivel);

  const vidaPrev = useRef(vida);
  const nivelPrev = useRef(nivel);
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [flashes, setFlashes] = useState<Array<{ id: number; intensidad: number }>>(
    [],
  );
  const [levelUps, setLevelUps] = useState<Array<{ id: number; nivel: number }>>([]);

  // Detectar daño: vida bajó respecto al render previo.
  useEffect(() => {
    const delta = vida - vidaPrev.current;
    if (delta < 0) {
      const danoRatio = Math.min(1, -delta / Math.max(1, vidaMax));
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setFlashes((arr) => [...arr, { id, intensidad: 0.3 + danoRatio * 0.55 }]);
      // sacarla después de que termine la animación
      setTimeout(() => {
        setFlashes((arr) => arr.filter((f) => f.id !== id));
      }, 900);

      // shake en el body: suma clase y la quita
      document.body.classList.add('shake-now');
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = setTimeout(() => {
        document.body.classList.remove('shake-now');
        shakeTimerRef.current = null;
      }, 500);
    }
    vidaPrev.current = vida;
  }, [vida, vidaMax]);

  // Detectar subida de nivel
  useEffect(() => {
    if (nivel > nivelPrev.current) {
      const id = Date.now();
      setLevelUps((arr) => [...arr, { id, nivel }]);
      setTimeout(() => {
        setLevelUps((arr) => arr.filter((l) => l.id !== id));
      }, 3500);
    }
    nivelPrev.current = nivel;
  }, [nivel]);

  useEffect(
    () => () => {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      document.body.classList.remove('shake-now');
    },
    [],
  );

  // Viñeta roja persistente de vida baja
  const ratio = vidaMax > 0 ? vida / vidaMax : 1;
  const enPeligro = ratio > 0 && ratio < 0.4;
  const intensidadPeligro = enPeligro
    ? Math.min(1, (0.4 - ratio) / 0.4) // 0..1 entre 40% y 0% de vida
    : 0;

  return (
    <>
      {/* Viñeta roja persistente — aparece al estar herido, pulsa */}
      {enPeligro && (
        <motion.div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-50"
          style={{
            background: `radial-gradient(ellipse at center, transparent ${
              70 - intensidadPeligro * 25
            }%, rgba(180, 20, 25, ${0.3 + intensidadPeligro * 0.45}) 100%)`,
          }}
          animate={{ opacity: [0.75, 1, 0.75] }}
          transition={{
            duration: Math.max(0.8, 2.2 - intensidadPeligro * 1.2),
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}

      {/* Flashes de daño */}
      <AnimatePresence>
        {flashes.map((f) => (
          <motion.div
            key={f.id}
            aria-hidden
            className="pointer-events-none fixed inset-0 z-50"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(225,40,45,0.55) 0%, rgba(150,20,25,0.4) 45%, transparent 80%)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, f.intensidad, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.85, ease: 'easeOut', times: [0, 0.2, 1] }}
          />
        ))}
      </AnimatePresence>

      {/* Banner de subida de nivel */}
      <AnimatePresence>
        {levelUps.map((lu) => (
          <LevelUpBanner key={lu.id} nivel={lu.nivel} />
        ))}
      </AnimatePresence>
    </>
  );
}

function LevelUpBanner({ nivel }: { nivel: number }) {
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* halo radial */}
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at center, rgba(92,145,73,0.35) 0%, rgba(92,145,73,0.08) 35%, transparent 65%)',
        }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: [0, 1, 0], scale: [0.6, 1.15, 1.3] }}
        transition={{ duration: 2, ease: 'easeOut' }}
      />

      {/* partículas orbitando */}
      {Array.from({ length: 14 }).map((_, i) => {
        const angle = (i / 14) * Math.PI * 2;
        const radius = 140 + (i % 3) * 40;
        const dx = Math.cos(angle) * radius;
        const dy = Math.sin(angle) * radius;
        return (
          <motion.span
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-moss-300 shadow-[0_0_6px_rgba(122,171,102,0.9)]"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{
              x: [0, dx * 0.6, dx],
              y: [0, dy * 0.6, dy],
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.4],
            }}
            transition={{ duration: 1.6, ease: 'easeOut', delay: 0.1 + i * 0.02 }}
          />
        );
      })}

      {/* texto central */}
      <motion.div
        className="relative text-center"
        initial={{ opacity: 0, y: 20, scale: 0.85 }}
        animate={{
          opacity: [0, 1, 1, 0],
          y: [20, 0, 0, -12],
          scale: [0.85, 1, 1, 0.98],
        }}
        transition={{ duration: 3.4, times: [0, 0.15, 0.75, 1], ease: 'easeOut' }}
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <Sparkles className="w-6 h-6 text-moss-300 animate-glow-pulse" />
          <p className="fluid-xs uppercase tracking-[0.5em] text-moss-300 font-display">
            Subiste de nivel
          </p>
          <Sparkles className="w-6 h-6 text-moss-300 animate-glow-pulse" />
        </div>
        <p className="fluid-3xl font-display tracking-[0.2em] text-ash-50">
          NIVEL {nivel}
        </p>
        <p className="mt-3 fluid-sm font-ui text-moss-200 uppercase tracking-widest">
          +1 punto de habilidad disponible
        </p>
      </motion.div>
    </motion.div>
  );
}
